// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  DialogContent,
  Grid,
  Stack,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { isAfter, isEqual } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { visitDetailsApi } from 'src/api/visit-details';
import Iconify from 'src/components/iconify';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { DARK_TEXT } from 'src/theme/palette';
import {
  GetVisitById,
  GetVisitByIdCancelledByEnum,
  GetVisitByIdStatusEnum,
  GetVisitByIdVisitTypeEnum,
  PaymentStatusEnum,
} from 'woofwoof-api';
import RejectPendingApprovalModal from './RejectPendingApprovalModal';
import VisitDetailsEditModal, {
  VisitDetailsEditInitialDocument,
  VisitDetailsEditPayload,
} from './VisitDetailsEditModal';
import VisitModalDetailsMembers from './VisitModalDetailsMembers';
import VisitModalDetailsTab from './VisitModalDetailsTab';
import VisitModalEditTab from './VisitModalEditTab';
import VisitModalFormsTab from './VisitModalFormsTab';

type VisitModalTabsProps = {
  visit?: GetVisitById;
  isConversationLoading?: boolean;
  onClose: () => void;
  isOpen: boolean;
  onReschedule?: ({
    professionalId,
    date,
    visitId,
  }: {
    professionalId: string;
    date: string;
    visitId: string;
  }) => void;
  isRescheduleLoading?: boolean;
  onCancel?: () => void;
  isCancelLoading?: boolean;
  editOnly?: boolean;
  onReject?: (visitId: string) => void;
  onApprove?: (visit: GetVisitById) => void;
  isApprovalLoading?: boolean;
  onJoinVideoOrPay?: (visitId: string) => void;
  /**
   * Called after a successful "Edytuj szczegóły" submit so the parent can
   * refetch the visit list (the modal mutates the visit description and
   * the documents tied to this visit).
   */
  onDetailsEdited?: () => void;
};

let isVisitTimeInterval: ReturnType<typeof setInterval>;
let isVisitCancelOrRescheduleAvailableInterval: ReturnType<typeof setInterval>;

export const NUMBER_OF_HOURS_BEFORE_LOCK = 6;

export const isTimeEqualOrPast = (inputTime: string): boolean => {
  const now = new Date();
  const visitTime = new Date(inputTime);

  return isEqual(now, visitTime) || isAfter(now, visitTime);
};

export default function VisitModalTabs({
  visit,
  isConversationLoading,
  onClose,
  isOpen,
  onReschedule,
  isRescheduleLoading,
  onCancel,
  isCancelLoading,
  editOnly,
  onReject,
  onApprove,
  isApprovalLoading,
  onJoinVideoOrPay,
  onDetailsEdited,
}: VisitModalTabsProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isDown500Px = useMediaQuery((theme) => theme.breakpoints.down(500));
  const [value, setValue] = useState(editOnly ? 2 : 1);
  const [isJoinEnabled, setIsJoinEnabled] = useState(false);
  const [isRescheduleOrCancelAvailable, setIsRescheduleOrCancelAvailable] =
    useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const isVisitCompleted = visit?.status === GetVisitByIdStatusEnum.Completed;
  // Treat ABORTED visits as cancelled for UI purposes — both are terminal
  // failures that block rebook/reschedule and show the same red styling.
  // `isVisitAborted` lets callers branch on the abort-specific copy where
  // it matters (e.g. the cancellation-reason banner).
  const isVisitAborted = visit?.status === GetVisitByIdStatusEnum.Aborted;
  const isVisitCancelled =
    visit?.status === GetVisitByIdStatusEnum.Cancelled || isVisitAborted;
  const isVisitRejected = visit?.status === GetVisitByIdStatusEnum.Rejected;
  const isVisitPending = visit?.status === GetVisitByIdStatusEnum.Pending;
  const isVisitWaitingForApproval =
    visit?.status === GetVisitByIdStatusEnum.PendingApproval;
  const isCreated = visit?.status === GetVisitByIdStatusEnum.Created;
  const isPaymentFailed = visit?.payment?.status === PaymentStatusEnum.Failed;
  const isPaymentPending = visit?.payment?.status === PaymentStatusEnum.Created;
  const isStartedByProfessional = (visit?.conversation as any)?.roomName;

  useEffect(() => {
    // If professional started the visit, enable join immediately
    if (isStartedByProfessional) {
      setIsJoinEnabled(true);
      return;
    }

    if (visit?.startTime) {
      const isVisitTime = isTimeEqualOrPast(visit.startTime);
      if (!isJoinEnabled && isVisitTime) {
        setIsJoinEnabled(true);
      }
    }
  }, [isJoinEnabled, visit?.startTime, isStartedByProfessional]);

  useEffect(() => {
    // If professional started the visit, no need for interval
    if (isStartedByProfessional) {
      return;
    }

    if (visit?.startTime) {
      isVisitTimeInterval = setInterval(() => {
        const isVisitTime = isTimeEqualOrPast(visit.startTime);
        if (!isJoinEnabled && isVisitTime) {
          setIsJoinEnabled(true);
        }
      }, 10000);
    }

    return () => {
      clearInterval(isVisitTimeInterval);
    };
  }, [isJoinEnabled, visit?.startTime, isStartedByProfessional]);

  useEffect(() => {
    if (visit?.startTime) {
      const visitTime = new Date(visit.startTime);
      const date = new Date(
        visitTime.setHours(visitTime.getHours() - NUMBER_OF_HOURS_BEFORE_LOCK),
      ).toISOString();
      const _isRescheduleOrCancelAvailable = !isTimeEqualOrPast(date);
      if (!isRescheduleOrCancelAvailable && _isRescheduleOrCancelAvailable) {
        setIsRescheduleOrCancelAvailable(true);
      }
    }
  }, [isRescheduleOrCancelAvailable, visit?.startTime]);

  useEffect(() => {
    if (visit?.startTime) {
      isVisitCancelOrRescheduleAvailableInterval = setInterval(() => {
        const visitTime = new Date(visit.startTime);
        const date = new Date(
          visitTime.setHours(
            visitTime.getHours() - NUMBER_OF_HOURS_BEFORE_LOCK,
          ),
        ).toISOString();
        const _isRescheduleOrCancelAvailable = !isTimeEqualOrPast(date);
        if (!isRescheduleOrCancelAvailable && _isRescheduleOrCancelAvailable) {
          setIsRescheduleOrCancelAvailable(true);
        }
      }, 2000);
    }

    return () => {
      clearInterval(isVisitCancelOrRescheduleAvailableInterval);
    };
  }, [isRescheduleOrCancelAvailable, visit?.startTime]);

  const tabs = useMemo(
    () =>
      isVisitCompleted
        ? [{ value: 1, label: t('visit.tabDetails'), disabled: false }]
        : [
            { value: 1, label: t('visit.tabDetails'), disabled: false },
            { value: 2, label: t('visit.tabManage'), disabled: false },
          ],
    [isVisitCompleted, t],
  );

  if (!visit) {
    return null;
  }

  // Status gating for the "Edytuj szczegóły" affordance — matches the
  // UpcomingVisitCard predicate so the action surfaces in the same states.
  const canEditDetails =
    visit.status === GetVisitByIdStatusEnum.Created ||
    visit.status === GetVisitByIdStatusEnum.Accepted;

  // Map the visit's documents into the shape the edit modal expects.
  // OpenAPI generator types this as `Array<object>`, so we narrow at the
  // boundary.
  const initialDocuments: VisitDetailsEditInitialDocument[] = (
    (visit.documents ?? []) as Array<{
      _id?: string;
      id?: string;
      filename: string;
      url: string;
      originalFilename?: string;
    }>
  )
    .filter((d) => d && (d._id || d.id))
    .map((d) => ({
      id: (d._id ?? d.id) as string,
      filename: d.filename,
      url: d.url,
      originalFilename: d.originalFilename,
    }));

  const handleDetailsSubmit = async (payload: VisitDetailsEditPayload) => {
    try {
      await visitDetailsApi.update(visit.id, payload);
      enqueueSnackbar(String(t('snackbar.visitUpdated')), {
        variant: 'success',
      });
      onDetailsEdited?.();
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : String(t('snackbar.genericError'));
      enqueueSnackbar(message, { variant: 'error' });
      throw err;
    }
  };

  const getActions = () => {
    if (isVisitWaitingForApproval) {
      return (
        <Stack gap={2} flex={1}>
          {isPaymentPending && (
            <Alert severity="warning">
              {t('visit.clinicBookedAwaitingPayment')}
            </Alert>
          )}
          {isPaymentFailed && (
            <Alert severity="error">{t('visit.paymentRejectedRetry')}</Alert>
          )}
          {!isPaymentPending && !isPaymentFailed && (
            <Alert severity="warning">
              {t('visit.clinicBookedAwaitingConfirm')}
            </Alert>
          )}
          <Stack
            sx={{
              flexDirection: 'row',
              justifyContent: 'flex-end',
              gap: 2,
              flex: 1,
            }}
          >
            <Stack
              flexDirection={isDown500Px ? 'column' : 'row'}
              gap={2}
              justifyContent="space-between"
              flex={1}
            >
              <Stack
                sx={{ flexDirection: isDown500Px ? 'column' : 'row', gap: 2 }}
              >
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={onClose}
                  fullWidth
                  size={isDown500Px ? 'large' : 'medium'}
                >
                  {t('quickBooking.close')}
                </Button>
              </Stack>

              <Stack
                sx={{ flexDirection: isDown500Px ? 'column' : 'row', gap: 2 }}
              >
                {onReject && (
                  <LoadingButton
                    variant="outlined"
                    color="error"
                    startIcon={<Iconify icon="ic:close" />}
                    onClick={() => setIsRejectModalOpen(true)}
                    disabled={isApprovalLoading}
                    fullWidth
                    size={isDown500Px ? 'large' : 'medium'}
                    data-testid="visit-reject-button"
                  >
                    {t('visit.rejectAction')}
                  </LoadingButton>
                )}
                {onApprove && (
                  <LoadingButton
                    variant="contained"
                    data-testid="visit-approve-button"
                    sx={{
                      background: theme.palette.common.black,
                      color: DARK_TEXT.primary,
                      minWidth: '150px',
                    }}
                    startIcon={
                      isPaymentPending || isPaymentFailed ? (
                        <Iconify icon="solar:wallet-money-bold" />
                      ) : (
                        <Iconify icon="ic:check" />
                      )
                    }
                    onClick={() => onApprove(visit)}
                    disabled={isApprovalLoading}
                    loading={isApprovalLoading}
                    fullWidth
                    size={isDown500Px ? 'large' : 'medium'}
                  >
                    {isPaymentPending
                      ? t('visit.payVisit')
                      : isPaymentFailed
                      ? t('visit.retryPayment')
                      : t('visit.confirmAction')}
                  </LoadingButton>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      );
    }

    if (
      visit.visitType === GetVisitByIdVisitTypeEnum.Video &&
      !isVisitCompleted &&
      !isVisitCancelled &&
      !isVisitRejected
    ) {
      return (
        <Stack gap={2} flex={1}>
          {isPaymentFailed && (
            <Alert severity="error">{t('visit.paymentRejectedRetry')}</Alert>
          )}
          {!isPaymentFailed && !isStartedByProfessional && isJoinEnabled && (
            <Alert severity="info">{t('visit.waitingForSpecialist')}</Alert>
          )}
          {!isPaymentFailed && !isJoinEnabled && !isPaymentPending && (
            <Alert severity="info">{t('visit.joinAtScheduledTime')}</Alert>
          )}
          {isPaymentPending && !isJoinEnabled && !isPaymentFailed && (
            <Alert severity="warning">{t('visit.paymentProcessing')}</Alert>
          )}
          {isJoinEnabled && isStartedByProfessional && (
            <Alert severity="success">
              {t('visit.specialistStartedJoin')}
            </Alert>
          )}

          <Stack
            sx={{
              flexDirection: isDown500Px ? 'column' : 'row',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <Stack
              flexDirection={isDown500Px ? 'column' : 'row'}
              gap={2}
              justifyContent="space-between"
              flex={1}
            >
              <Button variant="outlined" color="inherit" onClick={onClose}>
                {t('quickBooking.close')}
              </Button>
              {onJoinVideoOrPay && (
                <LoadingButton
                  variant="contained"
                  sx={{
                    background: theme.palette.common.black,
                    color: DARK_TEXT.primary,
                    minWidth: '180px',
                  }}
                  startIcon={
                    isPaymentFailed ? (
                      <Iconify icon="solar:wallet-money-bold" />
                    ) : (
                      <Iconify icon="solar:videocamera-record-bold" />
                    )
                  }
                  disabled={
                    (isCreated && !isPaymentFailed) ||
                    isPaymentPending ||
                    (!isStartedByProfessional && !isPaymentFailed) ||
                    (!isJoinEnabled && !isPaymentFailed) ||
                    isCancelLoading ||
                    isRescheduleLoading
                  }
                  size={isDown500Px ? 'large' : 'medium'}
                  fullWidth
                  onClick={() => onJoinVideoOrPay(visit.id)}
                >
                  <span>
                    {isPaymentFailed
                      ? t('visit.retryPayment')
                      : isStartedByProfessional
                      ? t('visit.joinAction')
                      : t('visit.startVisit')}
                  </span>
                </LoadingButton>
              )}
            </Stack>
          </Stack>
        </Stack>
      );
    }

    return (
      <Stack sx={{ flex: 1, gap: 2 }}>
        {isVisitCancelled &&
          (visit.cancellationReason || visit.cancelledBy) && (
            <Alert severity="warning">
              {isVisitAborted ? (
                <>
                  {t('visit.abortedByProfessional')}
                  {visit.cancellationReason && '. '}
                </>
              ) : (
                visit.cancelledBy && (
                  <>
                    {t('visit.cancelledByLabel')}{' '}
                    {visit.cancelledBy === GetVisitByIdCancelledByEnum.Owner
                      ? t('visit.cancelledByOwner')
                      : t('visit.cancelledByClinic')}
                    {visit.cancellationReason && '. '}
                  </>
                )
              )}
              {visit.cancellationReason}
            </Alert>
          )}
        <Stack
          sx={{
            flexDirection: isDown500Px ? 'column-reverse' : 'row',
            justifyContent: 'space-between',
            gap: 2,
            flex: 1,
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            fullWidth={isDown500Px}
            size={isDown500Px ? 'large' : 'medium'}
          >
            {t('quickBooking.close')}
          </Button>
          {(isVisitCompleted || isVisitCancelled || isVisitRejected) &&
            visit?.organization && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  onClose();
                  navigate(
                    `${PATH_DASHBOARD.general.search.organization(
                      visit.organization?.slug || visit.organization?.id || '',
                    )}?type=${visit.visitType}`,
                  );
                }}
                fullWidth={isDown500Px}
                size={isDown500Px ? 'large' : 'medium'}
              >
                {t('visit.bookAgain')}
              </Button>
            )}
          {canEditDetails && (
            <Button
              variant="contained"
              data-testid="visit-edit-details-open"
              sx={{
                background: theme.palette.common.black,
                color: DARK_TEXT.primary,
              }}
              onClick={() => setIsDetailsModalOpen(true)}
              fullWidth={isDown500Px}
              size={isDown500Px ? 'large' : 'medium'}
            >
              {t('visit.editDetailsAction')}
            </Button>
          )}
        </Stack>
      </Stack>
    );
  };

  return (
    <>
      <DialogContent sx={{ py: 3 }}>
        <Stack sx={{ gap: 3, width: '100%' }}>
          {!isVisitCompleted &&
            !isVisitCancelled &&
            !isVisitPending &&
            !isVisitWaitingForApproval &&
            !isPaymentFailed &&
            !isPaymentPending &&
            !editOnly && (
              <Stack
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  borderWidth: '1px',
                  borderColor: theme.palette.divider,
                  borderBottomStyle: 'solid',
                  gap: 2,
                  mx: { xs: 0, sm: 0, md: 0, lg: 0 },
                  width: '100%',
                }}
              >
                <Tabs
                  value={value}
                  onChange={(_, newValue) => setValue(newValue)}
                  variant="fullWidth"
                  sx={{ width: '100%' }}
                >
                  {tabs.map((tab) => (
                    <Tab
                      key={tab.value}
                      label={tab.label}
                      value={tab.value}
                      disabled={tab.disabled}
                      sx={{ fontSize: 16 }}
                      data-testid={`visit-tab-${tab.value}`}
                    />
                  ))}
                </Tabs>
              </Stack>
            )}
          <VisitModalDetailsMembers visit={visit} />
          <Grid item container spacing={3}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              {value === 1 && (
                <>
                  <VisitModalDetailsTab visit={visit} />
                </>
              )}
              {value === 2 && (
                <>
                  <VisitModalEditTab
                    visit={visit}
                    onClose={onClose}
                    isOpen={value === 2 && isOpen}
                    onReschedule={onReschedule}
                    isRescheduleLoading={isRescheduleLoading}
                    onCancel={onCancel}
                    isCancelLoading={isCancelLoading}
                    isRescheduleOrCancelAvailable={
                      isRescheduleOrCancelAvailable
                    }
                  />
                </>
              )}
              {value === 3 && <VisitModalFormsTab visit={visit} />}
            </Grid>
            {value === 1 && (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Stack
                  sx={{
                    flexDirection: 'row',
                    gap: 1.5,
                    justifyContent: 'flex-end',
                    flex: 1,
                    width: '100%',
                  }}
                >
                  {getActions()}
                </Stack>
              </Grid>
            )}
          </Grid>
        </Stack>
      </DialogContent>
      <RejectPendingApprovalModal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        onAccept={() => {
          onReject?.(visit.id);
          setIsRejectModalOpen(false);
        }}
        isLoading={isApprovalLoading}
      />
      <VisitDetailsEditModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        initialDescription={visit.description ?? ''}
        initialDocuments={initialDocuments}
        onSubmit={handleDetailsSubmit}
        visitId={visit.id}
      />
    </>
  );
}
